import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateDepartment, useUpdateDepartment } from '../../hooks/use-departments';
import { departmentFormSchema, DepartmentFormValues } from '../../schemas/department-form-schema';
import { useCurrentOrganizationName } from '@/hooks/use-organizations';
import { buildDepartmentKey } from '@/lib/department-scope';
import { useAuthStore } from '@/state/store/auth';
import { departmentKeyExists } from '../../services/department.service';
import { Department } from '../../types/department.types';

export interface DepartmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  department?: Department | null;
}

/**
 * Single dialog for both create and edit. Key is generated from Title on create and immutable after.
 */
export const DepartmentFormDialog = ({
  open,
  onOpenChange,
  department,
}: DepartmentFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(department);

  const { mutateAsync: createDepartment, isPending: isCreating } = useCreateDepartment();
  const { mutateAsync: updateDepartment, isPending: isUpdating } = useUpdateDepartment();
  const isSubmitting = isCreating || isUpdating;
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? '';
  const { data: organizationName } = useCurrentOrganizationName();
  // The Key carries the organization's name, so a new department can't be created before it's known.
  const isWaitingForOrganization = !isEditMode && !organizationName;

  const form = useForm<DepartmentFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(departmentFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Title: '', Key: '', Description: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      Title: department?.Title ?? '',
      Key: department?.Key ?? '',
      Description: department?.Description ?? '',
    });
  }, [open, department, form]);

  // Key is never typed by hand — it IS the slug of this department's Head of Department role
  // (`hrm::department-scope::<organization-slug>::<department-slug>`), generated live from the
  // organization's name and Title, not a short code that gets prefixed later. Only while creating:
  // an existing department's Key is immutable, so editing Title afterward must not silently change it.
  const titleValue = useWatch({ control: form.control, name: 'Title' });
  useEffect(() => {
    if (isEditMode || !organizationName) return;
    form.setValue('Key', buildDepartmentKey(titleValue ?? '', organizationName, organizationId), {
      shouldValidate: true,
    });
  }, [titleValue, organizationName, organizationId, isEditMode, form]);

  const handleSubmit = async (values: DepartmentFormValues) => {
    try {
      if (isEditMode && department) {
        await updateDepartment({
          itemId: department.ItemId,
          input: {
            Title: values.Title,
            Description: values.Description,
          },
        });
      } else {
        if (await departmentKeyExists(values.Key)) {
          form.setError('Key', { message: t('DEPARTMENT_KEY_ALREADY_EXISTS') });
          return;
        }
        if (!organizationName) return;
        await createDepartment({ input: values, organizationName });
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_DEPARTMENT') : t('CREATE_DEPARTMENT')}</DialogTitle>
          {isEditMode && <DialogDescription>{t('DEPARTMENT_KEY_LOCKED')}</DialogDescription>}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="Title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DEPARTMENT_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('DEPARTMENT_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DEPARTMENT_KEY')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('DEPARTMENT_KEY')} {...field} disabled />
                  </FormControl>
                  <FormDescription>{t('DEPARTMENT_KEY_AUTO_GENERATED')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DESCRIPTION')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder={t('DESCRIPTION')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || isWaitingForOrganization}
              >
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
